import type { B2BCustomer } from "@/types/global"

export type TradeStatus = "guest" | "pending" | "approved"

export const getTradeStatus = (customer: B2BCustomer | null | undefined): { status: TradeStatus; tier?: string } => {
  if (!customer) return { status: "guest" }
  const tierGroup = ((customer as any).groups ?? []).find((g: any) => typeof g?.name === "string" && g.name.startsWith("Tier: "))
  return tierGroup ? { status: "approved", tier: tierGroup.name.replace("Tier: ", "") } : { status: "pending" }
}

export const aud = (n: number) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n)

/** RRP from Shopify import is GST-inclusive; returns ex-GST RRP for margin maths. */
export const rrpExGst = (rrp: number, hasGst: boolean) => (hasGst ? rrp / 1.1 : rrp)

/** RRP inc GST from variant metadata. Accepts numeric strings: the Medusa admin metadata editor (and, until
 * 25 Sep 2026, the Claude connector) saves "352.00" -- a strict typeof check showed those as "Trade only". */
export const rrpOf = (variant: any): number | null => {
  const r = variant?.metadata?.rrp
  const n = typeof r === "number" ? r : typeof r === "string" && /^\s*\d+(\.\d+)?\s*$/.test(r) ? parseFloat(r) : NaN
  return Number.isFinite(n) && n > 0 ? n : null
}
/** GST-free flag: has_gst false (boolean or "false" string). */
export const isGstFree = (variant: any) => variant?.metadata?.has_gst === false || variant?.metadata?.has_gst === "false"

export const variantTradeInfo = (variant: any) => {
  const rrp = rrpOf(variant)
  const hasGst = !isGstFree(variant)
  const cp = variant?.calculated_price
  const isTrade = cp?.calculated_price?.price_list_type === "sale"
  const trade = isTrade && typeof cp?.calculated_amount === "number" ? (cp.calculated_amount as number) : null
  const margin = rrp && trade ? (rrpExGst(rrp, hasGst) - trade) / rrpExGst(rrp, hasGst) : null
  return { rrp, hasGst, trade, margin }
}

// Single-serve sachets (<=5g: 1.6g, 2.5g, 5g...) never set a card's "From" price -- the smallest real size does.
// A product that is only a sachet still prices off it.
export const isSachet = (v: any) => {
  const t = String(v?.title ?? "")
  const g = t.match(/^\s*(\d+(?:\.\d+)?)\s*g\s*$/i)
  return /sachet/i.test(`${t} ${v?.sku ?? ""}`) || (!!g && parseFloat(g[1]) <= 5)
}

export const productTradeInfo = (product: any) => {
  const all = product?.variants ?? []
  const main = all.filter((v: any) => !isSachet(v))
  const infos = (main.length ? main : all).map(variantTradeInfo)
  const traded = infos.filter((i: any) => i.trade !== null).sort((a: any, b: any) => a.trade - b.trade)
  const withRrp = infos.filter((i: any) => i.rrp !== null).sort((a: any, b: any) => a.rrp - b.rrp)
  const best = traded[0] ?? null
  return {
    fromTrade: best?.trade ?? null,
    margin: best?.margin ?? null,
    rrp: best?.rrp ?? withRrp[0]?.rrp ?? null,
    hasGst: (best ?? withRrp[0] ?? infos[0])?.hasGst ?? true,
    multi: all.length > 1,
  }
}
